import {VideoPlayer, VideoPlayerProps} from "@buildwitt/shapeshift-common/v1/videoPlayer";
import {useContext, useEffect, useRef, useState, forwardRef} from "react";
import {makeStyles} from "tss-react/mui";
import {ReactPlayerProps} from "react-player";
import {VideoPlayerRef} from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";
import {PlayerAPI} from "bitmovin-player/modules/bitmovinplayer-core";
import {App2NotificationContext, App2NotificationSeverity} from "@/app2/shared/contexts/App2NotificationContext";
import {LocaleContext} from "@/app2/shared/contexts";
import {VideoProviders} from "@buildwitt/shapeshift-common/v2/videoPlayer/types";
import {OnProgressProps} from "react-player/base";
import {brightcovePlayerConfig} from "@/constants";
import {useEventCallback} from "@mui/material";
import {
	LESSON_VIDEO_NEAR_END_TOLERANCE_SEC,
	LESSON_VIDEO_SEEK_AT_END_OFFSET_SEC
} from "@/app2/pages/ModulePage/LessonPage/LessonVideoPlayer";

export interface App2AnnouncementVideoPlayerSource {
	videoUrl?: string | null;
	videoType?: string | null;
	videoThumbnailUrl?: string | null;
	complete?: boolean;
}

export type App2AnnouncementVideoPlayerProps = Omit<ReactPlayerProps, "url"> & {
	announcement?: App2AnnouncementVideoPlayerSource;
	bitmovinPlayer?: PlayerAPI | null;
	/** Brightcove: every valid timeupdate (for flush on unmount). */
	onPlaybackTime?: (seconds: number) => void;
};

const SKIP_MESSAGE = "Skipping is not permitted for incomplete announcements";

function isBrightcovePlayerActive(player: videojs.VideoJsPlayer | null | undefined): boolean {
	if (!player) {
		return false;
	}
	try {
		if (typeof player.isDisposed === "function" && player.isDisposed()) {
			return false;
		}
		return Boolean(player.el?.());
	} catch {
		return false;
	}
}

function safeBrightcoveSeek(player: videojs.VideoJsPlayer, seconds: number): void {
	if (!isBrightcovePlayerActive(player)) {
		return;
	}
	try {
		player.currentTime(seconds);
	} catch {
		// Player torn down while modal closes
	}
}

const useStyles = makeStyles()((theme) => ({
	videoPlayerBackdrop: {
		position: "relative",
		width: "100%",
		aspectRatio: "16 / 9",
		overflow: "hidden",
		background: "black",
		boxSizing: "border-box",
		[theme.breakpoints.down("sm")]: {
			aspectRatio: "16 / 9"
		}
	},
	videoPlayer: {
		zIndex: 0,
		background: "black",
		position: "absolute",
		opacity: 0,
		left: 0,
		top: 0,
		transition: "opacity .5s ease-in",
		right: "0px",
		width: "100%",
		height: "100% !important"
	}
}));

export const App2AnnouncementVideoPlayer = forwardRef<VideoPlayerRef, App2AnnouncementVideoPlayerProps>(
	function App2AnnouncementVideoPlayer(
		{announcement, currentTime, onReady, bitmovinPlayer, onPlaybackTime, ...props},
		externalRef
	) {
		const {classes} = useStyles();
		const [videoReady, setVideoReady] = useState(false);
		const {notify} = useContext(App2NotificationContext);
		const [localeState] = useContext(LocaleContext);
		const internalRef = useRef<VideoPlayerRef>({});
		const bcMaxTime = useRef<number | null>(null);
		const resumeTimeRef = useRef(currentTime);
		const onVideoFinishedRef = useRef(props.onVideoFinished);
		const onEndedRef = useRef(props.onEnded);
		/** Guards one-time Brightcove setup on loadedmetadata (not canplay — see onLoadedMetadata). */
		const hasLoadedMetadataHandled = useRef(false);
		const resumeSeekAppliedRef = useRef(false);
		const mountedRef = useRef(true);
		const bcPlayerRef = useRef<videojs.VideoJsPlayer | null>(null);

		resumeTimeRef.current = currentTime;
		onVideoFinishedRef.current = props.onVideoFinished;
		onEndedRef.current = props.onEnded;

		const locale = localeState?.locale;

		const handleSaveProgress = useEventCallback((time: number): void => {
			props.onProgress?.({playedSeconds: time});
		});

		const handleAllowComplete = useEventCallback((): void => {
			if (!announcement?.complete) {
				props.onVideoFinished?.();
			}
		});

		const isAnnouncementComplete = useEventCallback((): boolean => {
			return !!announcement?.complete;
		});

		const handleReady: VideoPlayerProps["onReady"] = useEventCallback((extPlayer) => {
			onReady?.();
			const resumeAtReady = resumeTimeRef.current;

			((bcPlayer?: videojs.VideoJsPlayer) => {
				if (!bcPlayer?.player || announcement?.videoType !== "brightcove" || !mountedRef.current) {
					return;
				}

				bcPlayerRef.current = bcPlayer;

				const saveProgressThrottled = (() => {
					let lastCall = 0;
					return (time: number) => {
						const now = Date.now();
						if (now - lastCall >= 5000) {
							lastCall = now;
							handleSaveProgress(time);
						}
					};
				})();

				bcMaxTime.current = resumeAtReady ?? null;

				const onTimeupdate = (): void => {
					if (!mountedRef.current || !isBrightcovePlayerActive(bcPlayerRef.current)) {
						return;
					}
					const player = bcPlayerRef.current!;
					const ct = player.currentTime();
					let validCurrentTime = ct;
					const MAX_JUMP = 1;
					if (!isAnnouncementComplete() && ct > (bcMaxTime.current ?? 0) + MAX_JUMP) {
						validCurrentTime = bcMaxTime.current ?? 0;
						safeBrightcoveSeek(player, validCurrentTime);
						notify(SKIP_MESSAGE, "", App2NotificationSeverity.warning, 5000, false);
						return;
					}
					bcMaxTime.current = Math.max(validCurrentTime, bcMaxTime.current ?? 0);
					onPlaybackTime?.(validCurrentTime);
					saveProgressThrottled(validCurrentTime);
					if (player.duration() - ct < 10) {
						handleAllowComplete();
					}
				};

				const onLoadedMetadata = (): void => {
					if (!mountedRef.current || !isBrightcovePlayerActive(bcPlayerRef.current)) {
						return;
					}
					if (hasLoadedMetadataHandled.current) {
						return;
					}
					hasLoadedMetadataHandled.current = true;
					const player = bcPlayerRef.current!;
					player.playbackRates([0.5, 0.75, 1, 1.25, 1.5]);

					// Match legacy announcement player: configure audio on loadedmetadata.
					// On canplay, track.language is often still empty and every track was set
					// enabled=false (mute). If nothing matches locale, leave Brightcove defaults.
					const audioTrackList = player.audioTracks?.();
					const tracks = audioTrackList ? Array.from(audioTrackList) : [];
					const userLang = (locale ?? "").split("-")[0].toLowerCase();
					if (!tracks.length || !userLang) {
						return;
					}
					let matched = false;
					for (const track of tracks) {
						const trackLang = (track.language ?? "").split("-")[0].toLowerCase();
						if (trackLang && trackLang === userLang && !matched) {
							track.enabled = true;
							matched = true;
						} else if (matched) {
							track.enabled = false;
						}
					}
				};

				let firstPlay = true;
				const onPlay = (): void => {
					if (!mountedRef.current || !isBrightcovePlayerActive(bcPlayerRef.current)) {
						return;
					}
					if (!firstPlay) {
						return;
					}
					firstPlay = false;
					const player = bcPlayerRef.current!;
					const duration = player.duration();
					const resume = resumeTimeRef.current;
					const resumeSec = resume ?? 0;
					if (
						Number.isFinite(duration) &&
						duration > 0 &&
						resume != null &&
						resumeSec >= duration - LESSON_VIDEO_NEAR_END_TOLERANCE_SEC
					) {
						const seekTo = Math.max(0, Math.min(resumeSec, duration - LESSON_VIDEO_SEEK_AT_END_OFFSET_SEC));
						bcMaxTime.current = seekTo;
						safeBrightcoveSeek(player, seekTo);
						try {
							player.pause();
							player.removeClass?.("vjs-waiting");
							player.addClass?.("vjs-ended");
						} catch {
							// ignore
						}
						onVideoFinishedRef.current?.();
						onEndedRef.current?.();
						return;
					}
					if (resume != null && resume > 0) {
						resumeSeekAppliedRef.current = true;
						bcMaxTime.current = Math.max(bcMaxTime.current ?? 0, resume);
						safeBrightcoveSeek(player, resume);
					}
				};

				// Resume seek runs on first play only (not on canplay) to avoid iOS mute side effects.
				bcPlayer.on("timeupdate", onTimeupdate);
				bcPlayer.on("loadedmetadata", onLoadedMetadata);
				bcPlayer.on("play", onPlay);
			})(extPlayer);

			if (mountedRef.current) {
				setVideoReady(true);
			}
		});

		useEffect(() => {
			mountedRef.current = true;
			resumeSeekAppliedRef.current = false;
			return () => {
				mountedRef.current = false;
				setVideoReady(false);
				hasLoadedMetadataHandled.current = false;
				resumeSeekAppliedRef.current = false;
				const bc = bcPlayerRef.current;
				bcPlayerRef.current = null;
				if (bc) {
					try {
						bc.off("timeupdate");
						bc.off("loadedmetadata");
						bc.off("play");
					} catch {
						// ignore
					}
				}
			};
		}, [announcement?.videoUrl]);

		useEffect(() => {
			if (videoReady && announcement?.videoType === "bitmovin" && currentTime && bitmovinPlayer) {
				bitmovinPlayer.seek(currentTime);
			}
		}, [currentTime, videoReady, bitmovinPlayer, announcement?.videoType]);

		useEffect(() => {
			if (!internalRef.current.react || announcement?.videoType !== "url") {
				return;
			}
			const playerInstance = internalRef.current.react.react?.player;
			if (videoReady && playerInstance && currentTime != null && currentTime > 0) {
				playerInstance.seekTo(currentTime, "seconds");
			}
		}, [currentTime, videoReady, announcement?.videoType]);

		const generateVideoRef = function <T extends { [x: string]: NonNullable<T> }>(
			current: T,
			type: VideoProviders
		): void | { [x: string]: NonNullable<T> } | null {
			if (type !== "loom") {
				internalRef.current[type] = current;
			}
			if (current && externalRef) {
				const refReturn =
					typeof externalRef === "function" ? externalRef(current) : (externalRef.current = current);
				if (refReturn != null) {
					return refReturn !== undefined && refReturn !== null
						? {[type]: refReturn as NonNullable<T>}
						: null;
				}
			}
			return null;
		};

		const prevPlayedSeconds = useRef<number>(0);
		useEffect(() => {
			prevPlayedSeconds.current = 0;
		}, [announcement?.videoUrl]);

		const handleSeek = ({playedSeconds}: OnProgressProps): void => {
			if (!internalRef.current.react) {
				return;
			}
			const playerInstance = internalRef.current.react.react?.player;
			if (!announcement || !videoReady || !playerInstance) {
				return;
			}

			const diff = playedSeconds - (prevPlayedSeconds.current ?? 0);
			if (prevPlayedSeconds.current > 0 && diff > 2 && !announcement.complete) {
				notify(SKIP_MESSAGE, "", App2NotificationSeverity.warning, 5000, false);
				playerInstance.seekTo(prevPlayedSeconds.current, "seconds");
				playerInstance.player?.play();
			} else {
				prevPlayedSeconds.current = playedSeconds;
			}
			if ((playerInstance.getDuration() ?? 0) - playedSeconds < 10) {
				props.onVideoFinished?.();
			}
			props.onProgress?.({playedSeconds});
		};

		if (!announcement?.videoUrl) {
			return null;
		}

		return (
			<div className={classes.videoPlayerBackdrop}>
				<VideoPlayer
					key={announcement.videoUrl}
					{...props}
					moduleVideoType={announcement.videoType as "bitmovin" | "url" | "brightcove" | "loom"}
					ref={
						announcement.videoType === "bitmovin"
							? externalRef
							: (current: unknown) =>
								generateVideoRef(current as {[x: string]: NonNullable<unknown>}, "react")
					}
					onReady={handleReady}
					className={classes.videoPlayer}
					style={{opacity: videoReady ? 1 : 0}}
					url={announcement.videoUrl}
					controls={true}
					providerDetails={{
						bitmovin: {player: bitmovinPlayer},
						brightcove: {
							...brightcovePlayerConfig,
							posterUrl: announcement.videoThumbnailUrl ?? undefined
						}
					}}
					onProgress={announcement.videoType === "url" ? handleSeek : undefined}
				/>
			</div>
		);
	}
);
